export type DebounceFunction<T extends any[]> = (...args: T) => void;

export class Debounced<T extends any[]> {
    private readonly func: DebounceFunction<T>;
    private delayMillis: number;
    private forceDebounceOnNextCall = false;

    private timer: ReturnType<typeof setTimeout> | null = null;
    private lastCallAtMillis = 0;

    constructor(func: DebounceFunction<T>, delayMillis: number) {
        this.delayMillis = delayMillis;
        this.func = func;
    }

    /**
     * Set the debounce delay for all subsequent calls.
     */
    public setDelayMillis(delayMillis: number): void {
        this.delayMillis = delayMillis;
    }

    /**
     * Force the next call to be debounced, even if enough time
     * has passed since the last call.
     */
    public setForceDebounceOnNextCall(value: boolean): void {
        this.forceDebounceOnNextCall = value;
    }

    public call(...args: T): void {
        this.callImpl(...args);
    }

    private callImpl(...args: T): void {
        if (! this.forceDebounceOnNextCall && this.timer === null) {
            const nowMillis = timestampMillis();
            const elapsedMsSinceLastCall = nowMillis - this.lastCallAtMillis;
            if (elapsedMsSinceLastCall >= this.delayMillis) {
                this.lastCallAtMillis = nowMillis;
                this.func(...args);
                return;
            }
        }

        this.forceDebounceOnNextCall = false;

        if (this.timer !== null) {
            clearTimeout(this.timer);
        }

        this.timer = setTimeout(() => {
            this.timer = null;
            this.lastCallAtMillis = timestampMillis();
            this.func(...args);
        }, this.delayMillis);
    }
}

function timestampMillis() {
    return Math.ceil(performance.now());
}
